import Popup from "react-animated-popup";
import { useTheme } from "../ThemeContext";
import { useLogout } from "./hooks";

/**
 * Professional popup component with enhanced styling and theming support.
 * Provides a clean, modern interface for displaying content with proper visual hierarchy.
 * 
 * @param {boolean} visible - Controls popup visibility
 * @param {React.ReactNode} content - Content to display in the popup
 * @param {Function} setVisible - Function to control popup visibility
 * @param {Function} setIsAuthenticated - Function to update authentication state
 */
const CustomPopup = ({ visible, content, setVisible, setIsAuthenticated }) => {
  const { currentTheme } = useTheme();
  const logout = useLogout();

  /**
   * Handles popup close action by logging out the user and updating authentication state
   */
  const handleLogout = () => {
    setVisible(false);
    logout();
    setIsAuthenticated(false);
  };

  return (
    <Popup
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        maxWidth: "90vw",
        width: "auto",
        minWidth: "320px",
        maxHeight: "90vh",
        background: currentTheme.loginPage?.forceLoginBgColor || "#ffffff",
        color: currentTheme.homePage?.textColor || "#262626",
        borderRadius: "12px",
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
        border: "none",
        overflow: "hidden",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        // Professional gradient overlay for better visual depth
        backgroundImage: `linear-gradient(135deg, 
          ${currentTheme.loginPage?.forceLoginBgColor || "#ffffff"} 0%, 
          ${currentTheme.loginPage?.forceLoginBgColor || "#fafafa"} 100%)`,
        // Smooth animations
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      visible={visible}
      onClose={() => handleLogout()}
      // Enhanced popup animation settings
      animationDuration={300}
      overlayStyle={{
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
    >
      <div style={{
        // Professional content wrapper with proper spacing
        padding: "0",
        width: "100%",
        position: "relative",
        // Ensure content is properly contained
        overflow: "hidden",
        borderRadius: "12px",
        // Professional typography
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
        lineHeight: "1.5",
        // Better text rendering
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
      }}>
        {content}
      </div>
    </Popup>
  );
};

export default CustomPopup;
